import { Dispatch, SetStateAction } from 'react';
import { deleteRemnaDevice } from '../cabinet.api';
import { CabinetView, DashboardState, RemnaDevice } from '../cabinet.schemas';
import { formatSlotLabel, getDevicesLimit, getFreeDeviceSlots } from '../cabinet.utils';

type DevicesPageProps = {
  dashboardState: DashboardState;
  setDashboardState: Dispatch<SetStateAction<DashboardState>>;
  onSelectView: (view: CabinetView) => void;
};

function getDeviceName(device: RemnaDevice) {
  return device.deviceModel || device.model || device.name || device.hwid || 'Устройство';
}

function getPlatformLabel(platform?: string) {
  const normalizedPlatform = platform?.toLowerCase() || '';

  if (normalizedPlatform.includes('ios') || normalizedPlatform.includes('iphone') || normalizedPlatform.includes('ipad')) {
    return 'iOS';
  }

  if (normalizedPlatform.includes('mac')) {
    return 'macOS';
  }

  if (normalizedPlatform.includes('android')) {
    return 'Android';
  }

  if (normalizedPlatform.includes('win')) {
    return 'Windows';
  }

  if (normalizedPlatform.includes('linux')) {
    return 'Linux';
  }

  return platform || 'Client';
}

function getPlatformTone(platform?: string) {
  const normalizedPlatform = platform?.toLowerCase() || '';

  if (normalizedPlatform.includes('ios') || normalizedPlatform.includes('mac')) {
    return 'apple';
  }

  if (normalizedPlatform.includes('android')) {
    return 'android';
  }

  if (normalizedPlatform.includes('win')) {
    return 'windows';
  }

  if (normalizedPlatform.includes('linux')) {
    return 'linux';
  }

  return 'default';
}

function getDeviceIconType(platform?: string) {
  const normalizedPlatform = platform?.toLowerCase() || '';

  if (
    normalizedPlatform.includes('ios')
    || normalizedPlatform.includes('iphone')
    || normalizedPlatform.includes('ipad')
    || normalizedPlatform.includes('android')
  ) {
    return 'phone';
  }

  return 'laptop';
}

function getUserAgentName(userAgent?: string) {
  return userAgent?.split(' ')[0] || 'Client';
}

function formatDeviceDate(createdAt?: string) {
  if (!createdAt) {
    return 'Дата подключения неизвестна';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return createdAt;
  }

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function DevicesPage({ dashboardState, setDashboardState, onSelectView }: DevicesPageProps) {
  const hasDevicesLimit = dashboardState.subInfo?.devices_count !== undefined && dashboardState.subInfo?.devices_count !== null;
  const devicesLimit = hasDevicesLimit ? getDevicesLimit(dashboardState.subInfo?.devices_count) : null;
  const apiDevices = dashboardState.devices;
  const devicesUsed = dashboardState.devicesTotal;
  const freeSlots = devicesLimit !== null ? getFreeDeviceSlots(devicesUsed, devicesLimit) : null;
  const slotsPercent = devicesLimit && devicesLimit > 0 ? Math.min((devicesUsed / devicesLimit) * 100, 100) : 0;

  const handleDeleteDevice = async (hwid?: string) => {
    if (!hwid) {
      return;
    }

    await deleteRemnaDevice(hwid);
    setDashboardState((currentState) => ({
      ...currentState,
      devices: currentState.devices.filter((device) => device.hwid !== hwid),
      devicesTotal: Math.max(currentState.devicesTotal - 1, 0),
    }));
  };

  return (
    <div className="cabinet-page">
      <div className="cabinet-title-row">
        <h1>Устройства</h1>
        {freeSlots !== null ? <span>{freeSlots === 0 ? 'Лимит заполнен' : `Свободно ${formatSlotLabel(freeSlots)}`}</span> : null}
      </div>
      <section className="device-page-list">
        {devicesLimit !== null && freeSlots !== null ? (
          <div className="device-summary-card">
            <div>
              <span>Слоты устройств</span>
              <strong>{devicesUsed} из {devicesLimit} занято</strong>
              <p>{freeSlots === 0 ? 'Все слоты для устройств заняты, удалите ненужное устройство' : `Можно подключить еще ${formatSlotLabel(freeSlots)}.`}</p>
            </div>
            <div className="device-slot-meter" aria-label={`Занято ${devicesUsed} из ${devicesLimit} устройств`}>
              <i style={{ width: `${slotsPercent}%` }} />
            </div>
          </div>
        ) : null}

        {apiDevices.length > 0 ? (
          <div className="device-card-grid">
            {apiDevices.map((device) => (
              <article className="device-card" key={device.hwid || getDeviceName(device)}>
                <div className={`device-platform-mark ${getPlatformTone(device.platform)}`} aria-label={getPlatformLabel(device.platform)}>
                  <span className={`device-platform-icon ${getDeviceIconType(device.platform)}`} aria-hidden="true" />
                </div>
                <div className="device-card-body">
                  <strong>{getDeviceName(device)}</strong>
                  <span>{getPlatformLabel(device.platform)}</span>
                  <span>{getUserAgentName(device.userAgent)}</span>
                  <small>Добавлено: {formatDeviceDate(device.createdAt)}</small>
                </div>
                <button className="danger-action" type="button" onClick={() => handleDeleteDevice(device.hwid)}>Удалить</button>
              </article>
            ))}
          </div>
        ) : (
          <div className="device-empty">
            <strong>Устройства пока не подключены</strong>
            <span>Откройте страницу подключения и импортируйте конфиг на нужной платформе.</span>
          </div>
        )}

        <button className="add-device-card" type="button" onClick={() => onSelectView('import')}>
          <span className="add-device-icon">+</span>
          <div>
            <strong>Добавить новое устройство</strong>
            <span>Скопируйте конфиг или откройте страницу подключения, чтобы запустить новый клиент.</span>
          </div>
        </button>
      </section>
    </div>
  );
}
